/**
 * @fileoverview This file contains the implementation of various store modules using Zustand.
 * Each store module fetches data from the corresponding service and updates the state using Zustand's `set` function.
 * The data fetching is done asynchronously using `async/await` syntax.
 * If an error occurs during data fetching, an error message is logged to the console.
 */
// src/store/index.js

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// OSNOVNI SIFARNICI KOJI SE KORISTE U APP
import JmService from '../service/JmService';
import UsersService from '../service/UsersService';
import ArtikalService from '../service/ArtikalService';
import ValutaService from '../service/ValutaService';
import VnoService from '../service/VnoService';
import GroupsService from '../service/GroupsService';
import KursService from '../service/KursService';
import ParamService from '../service/ParamService';
import StatusService from '../service/StatusService';
import MrsService from '../service/MrsService';
import NamenaPotrosnjeService from '../service/NamenaPotrosnjeService';
import RvpService from '../service/RvpService';
import SiService from '../service/SiService';
import NiService from '../service/NiService';
import TipTrosilaService from '../service/TipTrosilaService';
import MarkaTrosilaService from '../service/MarkaTrosilaService';
import PpService from '../service/PpService';

// usePp
export const usePp = create(
    persist(
        (set) => ({
            distributer: [],
            setDistributer: (distributer) => set({ distributer }),
            fetchDistributer: async () => {
                try {
                    const getDistributerGasa = PpService().getDistributerGasa;
                    const response = await getDistributerGasa().then((res) => res.data);
                    // console.log('Fetching Distributer data from server...', response);
                    set({ distributer: response });
                } catch (error) {
                    console.error('Error fetching Distributer data:', error);
                }
            },
            korisnikAplikacije: [],
            setKorisnikAplikacije: (korisnikAplikacije) => set({ korisnikAplikacije }),
            fetchKorisnikAplikacije: async () => {
                try {
                    const getKorisnikAplikacije = PpService().getKorisnikAplikacije;
                    const response = await getKorisnikAplikacije().then((res) => res.data);
                    // console.log('Fetching Korisnik Aplikacije data from server...', response);
                    set({ korisnikAplikacije: response });
                } catch (error) {
                    console.error('Error fetching Korisnik Aplikacije data:', error);
                }
            }
        }),
        {
            name: 'pp-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useMarkaTrosila
export const useMarkaTrosila = create(
    persist(
        (set) => ({
            markaTrosilas: [],
            setMarkaTrosilas: (markaTrosilas) => set({ markaTrosilas }),
            fetchMarkaTrosilas: async () => {
                try {
                    const getMarkaTrosilas = MarkaTrosilaService().getMarkaTrosilas;
                    const response = await getMarkaTrosilas((res) => res.data);
                    // console.log('Fetching Marka Trosila data from server...', response.data);
                    set({ markaTrosilas: response.data });
                } catch (error) {
                    console.error('Error fetching Marka Trosila data:', error);
                }
            }
        }),
        {
            name: 'marka-trosila-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useTipTrosila
export const useTipTrosila = create(
    persist(
        (set) => ({
            tipTrosilas: [],
            setTipTrosilas: (tipTrosilas) => set({ tipTrosilas }),
            fetchTipTrosilas: async () => {
                try {
                    const getTipTrosilas = TipTrosilaService().getTipTrosilas;
                    const response = await getTipTrosilas((res) => res.data);
                    // console.log('Fetching Tip Trosila data from server...', response.data);
                    set({ tipTrosilas: response.data });
                } catch (error) {
                    console.error('Error fetching Tip Trosila data:', error);
                }
            }
        }),
        {
            name: 'tip-trosila-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useNi
export const useNi = create(
    persist(
        (set) => ({
            nis: [],
            setNis: (nis) => set({ nis }),
            fetchNis: async () => {
                try {
                    const getNis = NiService().getNis;
                    const response = await getNis((res) => res.data);
                    // console.log('Fetching Ni data from server...', response.data);
                    set({ nis: response.data });
                } catch (error) {
                    console.error('Error fetching Ni data:', error);
                }
            }
        }),
        {
            name: 'ni-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useSi
export const useSi = create(
    persist(
        (set) => ({
            sis: [],
            setSis: (sis) => set({ sis }),
            fetchSis: async () => {
                try {
                    const getSis = SiService().getSis;
                    const response = await getSis((res) => res.data);
                    // console.log('Fetching Si data from server...', response.data);
                    set({ sis: response.data });
                } catch (error) {
                    console.error('Error fetching Si data:', error);
                }
            }
        }),
        {
            name: 'si-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useRvp
export const useRvp = create(
    persist(
        (set) => ({
            rvps: [],
            setRvps: (rvps) => set({ rvps }),
            fetchRvps: async () => {
                try {
                    const getRvps = RvpService().getRvps;
                    const response = await getRvps((res) => res.data);
                    // console.log('Fetching Rvp data from server...', response.data);
                    set({ rvps: response.data });
                } catch (error) {
                    console.error('Error fetching Rvp data:', error);
                }
            }
        }),
        {
            name: 'rvp-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useNamenaPotrosnje
export const useNamenaPotrosnje = create(
    persist(
        (set) => ({
            namenaPotrosnjes: [],
            setNamenaPotrosnjes: (namenaPotrosnjes) => set({ namenaPotrosnjes }),
            fetchNamenaPotrosnjes: async () => {
                try {
                    const getNamenaPotrosnjes = NamenaPotrosnjeService().getNamenaPotrosnjes;
                    const response = await getNamenaPotrosnjes((res) => res.data);
                    // console.log('Fetching Namena Potrosnje data from server...', response.data);
                    set({ namenaPotrosnjes: response.data });
                } catch (error) {
                    console.error('Error fetching Namena Potrosnje data:', error);
                }
            }
        }),
        {
            name: 'namena-potrosnje-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useMrs
export const useMrs = create(
    persist(
        (set) => ({
            mrss: [],
            setMrss: (mrss) => set({ mrss }),
            fetchMrss: async () => {
                try {
                    const getMrss = MrsService().getMrss;
                    const response = await getMrss((res) => res.data);
                    // console.log('Fetching Mrs data from server...', response.data);
                    set({ mrss: response.data });
                } catch (error) {
                    console.error('Error fetching Mrs data:', error);
                }
            }
        }),
        {
            name: 'mrs-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useParam
export const useParam = create(
    persist(
        (set) => ({
            params: [],
            setParams: (params) => set({ params }),
            fetchParams: async () => {
                try {
                    const getParams = ParamService().getParams;
                    const response = await getParams((res) => res.data);
                    // console.log('Fetching Param data from server...', response.data);
                    set({ params: response.data });
                } catch (error) {
                    console.error('Error fetching Param data:', error);
                }
            }
        }),
        {
            name: 'param-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useStatus
export const useStatus = create(
    persist(
        (set) => ({
            statuss: [],
            setStatuss: (statuss) => set({ statuss }),
            fetchStatuss: async () => {
                try {
                    const getStatuss = StatusService().getStatuss;
                    const response = await getStatuss((res) => res.data);
                    // console.log('Fetching Status data from server...', response.data);
                    set({ statuss: response.data });
                } catch (error) {
                    console.error('Error fetching Status data:', error);
                }
            }
        }),
        {
            name: 'status-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useKurs
export const useKurs = create(
    persist(
        (set) => ({
            kurs: [],
            setKurs: (kurs) => set({ kurs }),
            fetchKurs: async () => {
                try {
                    const getKurss = KursService().getKurss;
                    const response = await getKurss((res) => res.data);
                    // console.log('Fetching Kurs data from server...', response.data);
                    set({ kurs: response.data });
                } catch (error) {
                    console.error('Error fetching Kurs data:', error);
                }
            }
        }),
        {
            name: 'kurs-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useGroups

export const useGroups = create(
    persist(
        (set) => ({
            groups: [],
            setGroups: (groups) => set({ groups }),
            fetchGroups: async () => {
                try {
                    const getGroupss = GroupsService().getGroupss;
                    const response = await getGroupss((res) => res.data);
                    // console.log('Fetching Groups data from server...', response.data);
                    set({ groups: response.data });
                } catch (error) {
                    console.error('Error fetching Groups data:', error);
                }
            }
        }),
        {
            name: 'groups-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useVno

export const useVno = create(
    persist(
        (set) => ({
            vnos: [],
            setVnos: (vnos) => set({ vnos }),
            fetchVnos: async () => {
                try {
                    const getVnos = VnoService().getVnos;
                    const response = await getVnos((res) => res.data);
                    // console.log('Fetching Vno data from server...', response.data);
                    set({ vnos: response.data });
                } catch (error) {
                    console.error('Error fetching Vno data:', error);
                }
            }
        }),
        {
            name: 'vno-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// useTabela nema servis samo 4 vrednosti faktura, pugi, pkgp, zm

export const useTabela = create(
    // bez persist
    (set) => ({
        tabelas: [
            { name: 'Faktura', value: 'faktura' },
            { name: 'Pugi', value: 'pugi' },
            { name: 'Pkgp', value: 'pkgp' },
            { name: 'Zamena merila', value: 'zm' }
        ],
        setTabela: (data) => set({ tabelas: { ...data } })
    })
);

// JedinicaMere STORE with persist and async fetch from API
export const useJm = create(
    persist(
        (set) => ({
            jms: [],
            setJms: (jms) => set({ jms }),
            fetchJms: async () => {
                try {
                    const getJms = JmService().getJms;
                    const response = await getJms((res) => res.data);
                    // console.log('Fetching JM data from server...', response.data);
                    set({ jms: response.data });
                } catch (error) {
                    console.error('Error fetching JM data:', error);
                }
            }
        }),
        {
            name: 'jm-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

export const useUsers = create(
    persist(
        (set) => ({
            users: [],
            setUsers: (users) => set({ users }),
            fetchUsers: async () => {
                try {
                    const getUsersSimple = UsersService().getUsersSimple;
                    const response = await getUsersSimple((res) => res.data);
                    // console.log('Fetching Users data from server...', response.data);
                    set({ users: response.data });
                } catch (error) {
                    console.error('Error fetching Users data:', error);
                }
            }
        }),
        {
            name: 'users-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

export const useArtikal = create(
    persist(
        (set) => ({
            artikli: [],
            setArtikli: (artikli) => set({ artikli }),
            fetchArtikli: async () => {
                try {
                    const getArtikals = ArtikalService().getArtikals;
                    const response = await getArtikals((res) => res.data);
                    // console.log('Fetching Artikal data from server...', response.data);
                    set({ artikli: response.data });
                } catch (error) {
                    console.error('Error fetching Artikal data:', error);
                }
            }
        }),
        {
            name: 'artikal-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// valuta
export const useValuta = create(
    persist(
        (set) => ({
            valute: [],
            setValute: (valute) => set({ valute }),
            fetchValute: async () => {
                try {
                    const getValutas = ValutaService().getValutas;
                    const response = await getValutas((res) => res.data);
                    // console.log('Fetching Valuta data from server...', response.data);
                    set({ valute: response.data });
                } catch (error) {
                    console.error('Error fetching Valuta data:', error);
                }
            }
        }),
        {
            name: 'valuta-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage)
        }
    )
);

// export damy store for now
export const useGasCheck = create(
    persist(
        (set) => ({
            // initial state
            user: null,
            setUser: (user) => set({ user })
        }),
        {
            name: 'gascheck-storage', // name of the storage (must be unique)
            storage: createJSONStorage(() => localStorage) // (optional) by default the 'localStorage' is used
        }
    )
);

// select Artikal store
export const useSelectArtikal = create((set) => ({
    selectedArtikal: false,
    showArtikalDialog: false,
    updateId: null,
    setUpdateID: (id) => {
        set({ updateId: id });
    },
    setShowArtikalDialog: (show) => set({ showArtikalDialog: show }),
    setSelectedArtikal: (artikal) => set({ selectedArtikal: artikal }),
    removeSelectedArtikal: () => set({ selectedArtikal: false }),
    dialogCallback: null,
    setDialogCallback: (callback) => set({ dialogCallback: callback }),
    setScanResult: (result) => set({ scanResult: [...result] })
}));

// store for navigation in the application

export const useAppNavigation = create((set) => ({
    curentModule: 'dashboard',
    setCurentModule: (module) => set({ curentModule: module }),
    curentItem: '',
    setCurentItem: (model) => set({ curentItem: model })
}));

// čuvanje stanja liste skeniranih barkodova
export const useScanResult = create((set) => ({
    scanResult: [],
    selectedDoc: false,

    showListDialog: false,
    setShowListDialog: (show) => set({ showListDialog: show }),
    dialogCallback: null,
    setDialogCallback: (callback) => set({ dialogCallback: callback }),
    setScanResult: (result) => set({ scanResult: [...result] }),
    addScanResult: (result) => {
        set((state) => ({
            scanResult: [...state.scanResult, result]
        }));
    },

    removeScanResult: (result) => {
        set((state) =>
            //  remove item from array by index
            ({ scanResult: state.scanResult.filter((_, index) => index !== result) })
        );
    },

    removeSelectedDoc: () => {
        set(() => ({ selectedDoc: false }));
    },
    // izamena kolcine za artikal na osnovu index
    setScanResultQuantity: (index, quantity) => {
        set((state) => {
            const scanResult = state.scanResult;
            scanResult[index].kolicina = quantity;
            scanResult[index].q = quantity;
            return { scanResult };
        });
    },
    removeAllScanResult: (result) => {
        set((state) => ({ scanResult: [] }));
    }
}));

// Osluškivanje promena i upis u local storage
useScanResult.subscribe(
    (scanResult) => {
        // Pamti promeu u localStorage
        localStorage.setItem('scanResult', JSON.stringify(scanResult.scanResult));
    },
    (state) => state.scanResult // Osluškujemo samo promene na scanResult
);


